import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Stack,
  Text,
  useColorModeValue,
  VStack,
  SimpleGrid,
  Badge,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Image,
} from "@chakra-ui/react";
import { IoMdArrowForward } from "react-icons/io";
import {
  FaCheckCircle,
  FaShieldAlt,
  FaRobot,
  FaNotesMedical,
} from "react-icons/fa";

import IconBox from "../pure/Icons/IconBox";

import Theme from "../../constants/Theme";
import Footer from "../Common/Footer";
import Header from "../Common/Header";

import { handleCTA } from "../../utils";

const HOW_IT_WORKS_STEPS = [
  {
    title: "Capture Conversation",
    description:
      "Securely record the patient-clinician dialogue during the visit using our ambient technology.",
    icon: FaRobot,
  },
  {
    title: "AI Processing",
    description:
      "Our advanced AI analyzes the conversation, extracts key medical information, and structures it.",
    icon: FaNotesMedical,
  },
  {
    title: "Review & Finalize",
    description:
      "Clinicians quickly review the AI-generated draft note, make edits if needed, and approve.",
    icon: FaCheckCircle,
  },
];

const FEATURE_TABS = [
  {
    title: "Ambient Capture",
    content:
      "Just talk naturally. Our AI listens in the background, capturing the full patient narrative without interrupting the flow of care. It handles multiple speakers and various accents.",
    image: "/assets/images/ambient-doc.gif",
  },
  {
    title: "Intelligent Structuring",
    content:
      "Go beyond simple transcription. The AI understands medical context, identifying diagnoses, medications, procedures, and more, organizing them into standard clinical sections (HPI, ROS, Assessment, Plan).",
    image: "/assets/images/smart-templates.gif",
  },
  {
    title: "EHR Integration",
    content:
      "Seamlessly push finalized notes directly into your existing EHR system (Epic, Cerner, Athena, etc.). Pull patient context dynamically to inform the AI.",
    image: "/assets/images/clinical-context.gif",
  },
];

const KEY_BENEFITS = [
  {
    stat: "50%+",
    label: "Reduction in Documentation Time",
    description: "Free up hours daily to focus on patients, not paperwork.",
  },
  {
    stat: "99%",
    label: "Clinical Accuracy Rate",
    description: "Reduce errors and ensure comprehensive notes every time.",
  },
  {
    stat: "35%",
    label: "Improvement in Clinician Satisfaction",
    description: "Combat burnout by automating the most tedious tasks.",
  },
];

function AIDocumentation() {
  const heroButtonBg = useColorModeValue("white", "gray.800");
  const bgGradient = "linear(to-r, teal.400, blue.500)";
  const sectionBg = useColorModeValue("gray.50", "gray.900");
  const featureBg = useColorModeValue("white", "gray.800");
  const statColor = useColorModeValue("teal.500", "teal.300");
  const patternBg = useColorModeValue(
    "url('/assets/patterns/pattern-dots.svg')",
    "url('/assets/patterns/dark-pattern-dots.svg')"
  );

  return (
    <Box>
      <Header minimal={false} />

      <Box
        as="section"
        bgGradient={bgGradient}
        position="relative"
        overflow="hidden"
        py={{ base: 20, md: 28 }}
        textAlign="center"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.08}
          backgroundImage={patternBg}
          backgroundSize="cover"
        />
        <Container maxW="container.lg" position="relative">
          <VStack spacing={6} align="center">
            <Badge
              px={4}
              py={1}
              bg="rgba(255,255,255,0.15)"
              color="white"
              fontSize="md"
              borderRadius="full"
              fontWeight="semibold"
            >
              AI Clinical Documentation
            </Badge>
            <Heading
              size="2xl"
              lineHeight="1.2"
              fontWeight="bold"
              color="white"
              maxW="container.md"
            >
              Focus on Your Patients, We'll Handle the Notes
            </Heading>
            <Text fontSize="xl" color="whiteAlpha.900" maxW="container.md">
              Discover how QIQ's ambient AI automatically creates accurate,
              comprehensive clinical documentation from patient conversations,
              slashing documentation time and clinician burnout.
            </Text>
            <HStack spacing={4} pt={4}>
              <Button
                size="lg"
                bg={heroButtonBg}
                color="teal.500"
                _hover={{ bg: "white", transform: "translateY(-2px)" }}
                borderRadius="full"
                px={8}
                fontWeight="bold"
                boxShadow="lg"
                rightIcon={<IoMdArrowForward />}
                onClick={() => handleCTA("signup")}
                transition="all 0.3s"
              >
                Get Started Free
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="whiteAlpha.400"
                borderRadius="full"
                px={8}
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
                fontWeight="medium"
                onClick={() => {
                  const featureSection =
                    document.getElementById("how-it-works");
                  featureSection?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                How It Works
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <Box as="section" py={20} id="how-it-works" bg={sectionBg}>
        <Container maxW="container.xl">
          <VStack spacing={16}>
            <Box textAlign="center" maxW="container.md" mx="auto">
              <Text
                color="teal.500"
                fontWeight="semibold"
                fontSize="lg"
                mb={3}
                letterSpacing="wide"
                textTransform="uppercase"
              >
                From Chaos to Clarity
              </Text>
              <Heading
                size="xl"
                mb={4}
                fontWeight="bold"
                color={useColorModeValue("gray.700", "white")}
              >
                The AI Documentation Workflow
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
              >
                See how simple it is to transform conversations into structured
                clinical notes in just three steps.
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} w="full">
              {HOW_IT_WORKS_STEPS.map((step, idx) => (
                <VStack
                  key={idx}
                  bg={featureBg}
                  borderRadius="xl"
                  boxShadow="md"
                  p={8}
                  spacing={4}
                  align="center"
                  textAlign="center"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-5px)", boxShadow: "lg" }}
                >
                  <IconBox
                    h="60px"
                    w="60px"
                    bg={Theme.STYLES.CUCUMBER_WATER}
                    borderRadius="lg"
                    color="teal.500"
                  >
                    <Icon as={step.icon} w={7} h={7} />
                  </IconBox>
                  <Heading
                    size="md"
                    color={useColorModeValue("gray.700", "white")}
                  >
                    {idx + 1}. {step.title}
                  </Heading>
                  <Text
                    fontSize="md"
                    color={useColorModeValue("gray.600", "gray.400")}
                  >
                    {step.description}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <Box as="section" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={16}>
            <Box textAlign="center" maxW="container.md" mx="auto">
              <Text
                color="teal.500"
                fontWeight="semibold"
                fontSize="lg"
                mb={3}
                letterSpacing="wide"
                textTransform="uppercase"
              >
                Core Capabilities
              </Text>
              <Heading
                size="xl"
                mb={4}
                fontWeight="bold"
                color={useColorModeValue("gray.700", "white")}
              >
                Explore the AI Features
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
              >
                Dive deeper into the intelligent features that power our
                automated documentation.
              </Text>
            </Box>

            <Tabs variant="soft-rounded" colorScheme="teal" w="full" isFitted>
              <TabList mb="1em">
                {FEATURE_TABS.map((tab, idx) => (
                  <Tab key={idx} fontWeight="semibold">
                    {tab.title}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                {FEATURE_TABS.map((tab, idx) => (
                  <TabPanel
                    key={idx}
                    p={8}
                    bg={featureBg}
                    borderRadius="xl"
                    boxShadow="md"
                  >
                    <Stack
                      direction={{ base: "column", lg: "row" }}
                      spacing={8}
                      align="center"
                    >
                      <Box flex="1">
                        <Heading size="lg" mb={4}>
                          {tab.title}
                        </Heading>
                        <Text fontSize="lg">{tab.content}</Text>
                      </Box>
                      <Box flex="1" maxW="450px">
                        <Image
                          src={tab.image}
                          alt={`${tab.title} illustration`}
                          borderRadius="lg"
                          boxShadow="lg"
                        />
                      </Box>
                    </Stack>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </VStack>
        </Container>
      </Box>

      <Box as="section" py={20} bg={sectionBg}>
        <Container maxW="container.lg">
          <VStack spacing={12}>
            <Box textAlign="center" maxW="container.md" mx="auto">
              <Heading
                size="xl"
                mb={4}
                fontWeight="bold"
                color={useColorModeValue("gray.700", "white")}
              >
                Real Results, Rapid ROI
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
              >
                Our AI documentation doesn't just save time; it delivers
                measurable improvements across your practice.
              </Text>
            </Box>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} w="full">
              {KEY_BENEFITS.map((benefit, idx) => (
                <VStack
                  key={idx}
                  p={6}
                  bg={featureBg}
                  borderRadius="lg"
                  boxShadow="md"
                  spacing={3}
                  align="center"
                  textAlign="center"
                >
                  <Text
                    fontSize="5xl"
                    fontWeight="bold"
                    color={statColor}
                    lineHeight="1"
                  >
                    {benefit.stat}
                  </Text>
                  <Text fontSize="lg" fontWeight="semibold">
                    {benefit.label}
                  </Text>
                  <Text color={useColorModeValue("gray.600", "gray.400")}>
                    {benefit.description}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      <Box as="section" py={20}>
        <Container maxW="container.lg">
          <Stack
            direction={{ base: "column", lg: "row" }}
            spacing={10}
            align="center"
            bg={featureBg}
            p={10}
            borderRadius="xl"
            boxShadow="lg"
          >
            <IconBox
              h="80px"
              w="80px"
              bg="teal.50"
              borderRadius="full"
              color="teal.500"
              display={{ base: "none", lg: "flex" }}
            >
              <Icon as={FaShieldAlt} w={10} h={10} />
            </IconBox>
            <Box flex="1">
              <Heading
                size="lg"
                mb={4}
                color={useColorModeValue("gray.700", "white")}
              >
                Built on Trust, Secured for Healthcare
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
                mb={6}
              >
                We prioritize the security and privacy of patient data. Our
                platform is fully HIPAA compliant, employs end-to-end
                encryption, and undergoes rigorous security audits.
              </Text>
              <HStack spacing={4}>
                <Badge
                  colorScheme="blue"
                  variant="solid"
                  px={3}
                  py={1}
                  borderRadius="md"
                >
                  Highly Secure
                </Badge>
                <Badge
                  colorScheme="green"
                  variant="solid"
                  px={3}
                  py={1}
                  borderRadius="md"
                >
                  Enterprise Security
                </Badge>
                <Badge
                  colorScheme="purple"
                  variant="solid"
                  px={3}
                  py={1}
                  borderRadius="md"
                >
                  Encrypted
                </Badge>
              </HStack>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Box as="section" py={20} bgGradient={bgGradient} position="relative">
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.1}
          backgroundImage={patternBg}
          backgroundSize="cover"
        />
        <Container maxW="container.lg" position="relative">
          <VStack spacing={8} textAlign="center">
            <Heading size="xl" color="white">
              Ready to Experience Effortless Documentation?
            </Heading>
            <Text fontSize="lg" color="whiteAlpha.900" maxW="container.md">
              Start your free trial today and see how QIQ's AI can transform
              your clinical workflow and give you back valuable time.
            </Text>
            <Button
              size="lg"
              bg="white"
              color="teal.500"
              _hover={{ bg: "white", transform: "translateY(-2px)" }}
              borderRadius="full"
              px={10}
              py={7}
              fontWeight="bold"
              rightIcon={<IoMdArrowForward />}
              onClick={() => handleCTA("signup")}
              boxShadow="lg"
            >
              Claim Your Free Trial
            </Button>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}

export default AIDocumentation;
